import { useEffect } from "react";

/**
 * Lightweight SEO helper — no dependency needed.
 * Sets document title, meta description, canonical, OG fields.
 */
export default function SEO({ title, description, canonical, image, noindex = false, jsonLd = null }) {
  useEffect(() => {
    if (title) document.title = title;
    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      const el = robots || document.head.appendChild(document.createElement("meta"));
      el.setAttribute("name", "robots"); el.setAttribute("content", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }

    // Per-page structured data (FAQPage, Service, ...) — one script tag, replaced on route change.
    const ld = document.head.querySelector("script[data-page-jsonld]");
    if (jsonLd) {
      const el = ld || document.head.appendChild(document.createElement("script"));
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("data-page-jsonld", "");
      el.textContent = JSON.stringify(jsonLd);
    } else if (ld) {
      ld.remove();
    }

    const setMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag || "meta");
        Object.entries(attrs).forEach(([k, v]) => k !== "tag" && el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => k !== "tag" && el.setAttribute(k, v));
      }
    };

    if (description) {
      setMeta('meta[name="description"]', { name: "description", content: description });
      setMeta('meta[property="og:description"]', { property: "og:description", content: description });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    if (title) {
      setMeta('meta[property="og:title"]', { property: "og:title", content: title });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    if (image) {
      setMeta('meta[property="og:image"]', { property: "og:image", content: image });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    }
    if (canonical) {
      let el = document.head.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", canonical);
    }
  }, [title, description, canonical, image, noindex, jsonLd]);

  return null;
}
